import { useChannelTalk } from '@repo/channel-talk';
import { BznavAppIds } from '@repo/common-utils';
import { useCommonRouter } from '@repo/platform';
import { BoxButton, Checkbox, Divider, List, MainTitle, semanticColor, StickyBottomWrapper, TextList, TextListGroup, useDialog } from '@repo/ui';
import { useAuthContext } from '@repo/user-session';
import { useState } from 'react';
import { createPortal } from 'react-dom';

import { APP_NAME } from '../constants';
import { useCiHistoryController } from '../hooks/use-ci-history-controller';

export const WithdrawConfirmContent = () => {
  const router = useCommonRouter();
  const { showMessenger } = useChannelTalk();
  const { authService, setStatusSignOut } = useAuthContext();
  const [isAgree, setIsAgree] = useState(false);
  const footerElement = document.getElementById('bznav-footer');
  useCiHistoryController('exit');
  const { showDialog } = useDialog();
  const serviceName = process.env.NEXT_PUBLIC_APP_ID ? APP_NAME[process.env.NEXT_PUBLIC_APP_ID as BznavAppIds] : '비즈넵';

  const handleWithdraw = async () => {
    const searchParams = new URLSearchParams(window.location.search);
    const verificationCode = searchParams.get('verification_code') || '';

    try {
      const { leaveReasonId, reason } = JSON.parse(sessionStorage.getItem('withdraw-reason') ?? '{}');
      if (!verificationCode || !leaveReasonId) {
        await showDialog({
          title: '비정상적인 접근이에요'
        });

        router.back();
        return;
      }

      const response = await authService?.signService.startWithdraw(verificationCode, leaveReasonId, reason);

      const withdrawResult = await response?.json();
      if (!response?.ok) {
        await showDialog({
          title: '회원 탈퇴에 실패했어요',
          description: withdrawResult.message ?? '비정상적인 접근으로 탈퇴를 실패했어요. 다시 시도해주세요.'
        });
        router.back();
        return;
      }

      await showDialog({
        title: `${serviceName} 탈퇴가 완료되었어요`,
        description: `지금까지 ${serviceName}을 이용해주셔서 감사합니다. 남겨주신 의견을 참고해 더 좋은 서비스를 만들겠습니다.`
      });
      setStatusSignOut(true);
      router.push('/');
    } catch (error) {
      console.error('Failed to withdraw:', error);
      await showDialog({
        title: '회원 탈퇴에 실패했어요',
        description: '잠시 후 다시 시도해주세요.'
      });
    }
  };

  return (
    <div>
      <MainTitle
        graphic={
          <div className={'py-2'}>
            <img
              src={`${process.env.NEXT_PUBLIC_RESOURCE_CENTER_URL}/refund/logos/logo_bznav-refund.png`}
              className={'h-7 w-fit object-contain'}
              alt={'refund logo'}
            />
          </div>
        }
        title={`${serviceName} 회원탈퇴 전에 아래 유의사항을 확인해주세요`}
      />
      <TextListGroup className={'pt-2 text-neutral-foreground-lowest'}>
        <TextList size={'medium'} variant={'bullet'} className={'break-all'} bulletColor={semanticColor.neutral.foreground.lowest}>
          탈퇴 후에는 해당 계정으로 {serviceName} 로그인이 어려워요.
        </TextList>
        <TextList size={'medium'} variant={'bullet'} className={'break-all'} bulletColor={semanticColor.neutral.foreground.lowest}>
          탈퇴한 계정은 저장된 개인정보가 영구적으로 삭제되어 복구가 불가능해요.
        </TextList>
        {process.env.NEXT_PUBLIC_APP_ID === 'bznav-refund' && (
          <TextList size={'medium'} variant={'bullet'} className={'break-all'} bulletColor={semanticColor.neutral.foreground.lowest}>
            탈퇴 후 신규 가입했다면, 이전 내역이 삭제되어 환급받았더라도 다시 예상 환급액이 조회될 수 있어요. 이미 환급받은 연도는 다시 진행이 어려워요.
          </TextList>
        )}
      </TextListGroup>
      <List
        leadingElement={
          <Checkbox
            isBox
            checked={isAgree}
            onClick={(event) => {
              event.stopPropagation();
              setIsAgree(!isAgree);
            }}
          />
        }
        size={'small'}
        title={'모든 유의사항을 확인했으며, 탈퇴 시 회원정보는 삭제되고 데이터 복구가 불가능함에 동의합니다.'}
        className={'text-neutral-foreground-main my-normal-large'}
        primaryAction={true}
        onClick={() => {
          setIsAgree(!isAgree);
        }}
      />
      <Divider />
      <div className={'py-normal-large'}>
        <span className={'text-neutral-foreground-low'}>추가로 궁금하신 점은 문의해주세요.</span>
        <BoxButton size={'xsmall'} variant={'tonal-secondary'} className={'ml-tiny-large'} onClick={showMessenger}>
          문의하기
        </BoxButton>
      </div>
      {footerElement &&
        createPortal(
          <StickyBottomWrapper className={'!sticky'}>
            <BoxButton size={'large'} className={'w-full'} disabled={!isAgree} onClick={handleWithdraw}>
              {serviceName} 탈퇴하기
            </BoxButton>
          </StickyBottomWrapper>,
          footerElement
        )}
    </div>
  );
};
